/**
 * グローバルエラーハンドリングサービス
 */

import store from '@src/store';
import type { ToastSeverity } from '@src/store/slices/toastSlice';
import { addToast } from '@src/store/slices/toastSlice';

// エラータイプの定義
enum ErrorType {
  NETWORK = 'NETWORK',
  VALIDATION = 'VALIDATION',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  NOT_FOUND = 'NOT_FOUND',
  SERVER = 'SERVER',
  CLIENT = 'CLIENT',
  UNKNOWN = 'UNKNOWN',
}

// エラーレベルの定義
enum ErrorLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical',
}

/**
 * エラーの詳細情報を格納する型
 * @description エラーに関連する追加情報を型安全に扱うための型定義
 */
type ErrorDetails = Record<string, unknown> | Error | unknown;

/**
 * カスタムエラークラス
 * @description アプリケーション全体で使用する統一されたエラー型
 * @example
 * ```ts
 * const error = new AppError(
 *   'ユーザーが見つかりません',
 *   ErrorType.NOT_FOUND,
 *   ErrorLevel.WARNING,
 *   'USER_NOT_FOUND',
 *   { userId: 123 }
 * );
 * ```
 */
export class AppError extends Error {
  public type: ErrorType;
  public level: ErrorLevel;
  public code?: string;
  public details?: ErrorDetails;
  public timestamp: Date;

  /**
   * AppErrorのコンストラクタ
   * @param message - エラーメッセージ
   * @param type - エラータイプ（デフォルト: UNKNOWN）
   * @param level - エラーレベル（デフォルト: ERROR）
   * @param code - エラーコード（任意）
   * @param details - エラーの詳細情報（任意）
   */
  constructor(
    message: string,
    type: ErrorType = ErrorType.UNKNOWN,
    level: ErrorLevel = ErrorLevel.ERROR,
    code?: string,
    details?: ErrorDetails
  ) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.level = level;
    this.code = code;
    this.details = details;
    this.timestamp = new Date();

    // スタックトレースの保持
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }
}

// エラーメッセージのマッピング
const ERROR_MESSAGES: Record<ErrorType, string> = {
  [ErrorType.NETWORK]: 'ネットワークエラーが発生しました。接続を確認してください。',
  [ErrorType.VALIDATION]: '入力内容に誤りがあります。',
  [ErrorType.AUTHENTICATION]: '認証に失敗しました。',
  [ErrorType.AUTHORIZATION]: 'アクセス権限がありません。',
  [ErrorType.NOT_FOUND]: 'リソースが見つかりません。',
  [ErrorType.SERVER]: 'サーバーエラーが発生しました。',
  [ErrorType.CLIENT]: 'クライアントエラーが発生しました。',
  [ErrorType.UNKNOWN]: '予期しないエラーが発生しました。',
};

/**
 * エラーハンドリングサービス
 * @description アプリケーション全体のエラーを統一的に処理するシングルトンサービス
 */
export class ErrorHandlerService {
  private static instance: ErrorHandlerService;
  private errorLog: AppError[] = [];
  private maxLogSize = 100;

  private constructor() {
    this.setupGlobalHandlers();
  }

  /**
   * シングルトンインスタンスの取得
   * @returns ErrorHandlerServiceのシングルトンインスタンス
   * @description アプリケーション全体で単一のエラーハンドラーインスタンスを使用します
   */
  public static getInstance(): ErrorHandlerService {
    if (!ErrorHandlerService.instance) {
      ErrorHandlerService.instance = new ErrorHandlerService();
    }
    return ErrorHandlerService.instance;
  }

  /**
   * グローバルエラーハンドラーのセットアップ
   * @description ウィンドウレベルのエラーイベントとPromise拒否イベントをキャッチします
   */
  private setupGlobalHandlers(): void {
    // Uncaught Errorのハンドリング
    window.addEventListener('error', (event) => {
      this.handleError(
        new AppError(event.message, ErrorType.CLIENT, ErrorLevel.ERROR, 'UNCAUGHT_ERROR', {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        })
      );
      event.preventDefault();
    });

    // Unhandled Promise Rejectionのハンドリング
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError(
        new AppError(
          event.reason?.message || 'Unhandled Promise Rejection',
          ErrorType.CLIENT,
          ErrorLevel.ERROR,
          'UNHANDLED_PROMISE',
          event.reason
        )
      );
      event.preventDefault();
    });
  }

  /**
   * エラーハンドリングのメインメソッド
   * @param error - 処理対象のエラーオブジェクト（ErrorまたはAppError）
   * @description エラーレベルに応じて適切な処理（ログ出力、トースト通知、サーバー報告）を実行します
   */
  public handleError(error: Error | AppError): void {
    const appError = this.normalizeError(error);

    // エラーログに追加
    this.addToLog(appError);

    // エラーレベルに応じた処理
    switch (appError.level) {
      case ErrorLevel.DEBUG:
        console.debug('[DEBUG]', appError);
        break;
      case ErrorLevel.INFO:
        console.info('[INFO]', appError);
        this.showToast(appError.message, 'info');
        break;
      case ErrorLevel.WARNING:
        console.warn('[WARNING]', appError);
        this.showToast(appError.message, 'warning');
        break;
      case ErrorLevel.ERROR:
        console.error('[ERROR]', appError);
        this.showToast(appError.message, 'error');
        break;
      case ErrorLevel.CRITICAL:
        console.error('[CRITICAL]', appError);
        this.showToast(appError.message, 'error');
        this.reportToServer(appError);
        break;
    }

    // 開発環境でのデバッグ情報出力
    if (process.env.NODE_ENV === 'development') {
      console.group('Error Details');
      console.log('Type:', appError.type);
      console.log('Code:', appError.code);
      console.log('Details:', appError.details);
      console.log('Stack:', appError.stack);
      console.groupEnd();
    }
  }

  /**
   * HTTPレスポンスエラーのハンドリング
   * @param response - HTTPレスポンスオブジェクト
   * @returns 生成されたAppErrorオブジェクト
   * @description HTTPステータスコードに基づいて適切なエラー型とメッセージを設定します
   */
  public handleHttpError(response: Response): AppError {
    let errorType: ErrorType;
    let errorLevel: ErrorLevel;
    let message: string;

    switch (response.status) {
      case 400:
        errorType = ErrorType.VALIDATION;
        errorLevel = ErrorLevel.WARNING;
        message = 'リクエストが不正です。';
        break;
      case 401:
        errorType = ErrorType.AUTHENTICATION;
        errorLevel = ErrorLevel.WARNING;
        message = '認証が必要です。';
        break;
      case 403:
        errorType = ErrorType.AUTHORIZATION;
        errorLevel = ErrorLevel.WARNING;
        message = 'アクセス権限がありません。';
        break;
      case 404:
        errorType = ErrorType.NOT_FOUND;
        errorLevel = ErrorLevel.WARNING;
        message = 'リソースが見つかりません。';
        break;
      case 500:
      case 502:
      case 503:
        errorType = ErrorType.SERVER;
        errorLevel = ErrorLevel.ERROR;
        message = 'サーバーエラーが発生しました。';
        break;
      default:
        errorType = ErrorType.UNKNOWN;
        errorLevel = ErrorLevel.ERROR;
        message = `HTTPエラー: ${response.status}`;
    }

    const error = new AppError(message, errorType, errorLevel, `HTTP_${response.status}`, {
      url: response.url,
      status: response.status,
      statusText: response.statusText,
    });

    this.handleError(error);
    return error;
  }

  /**
   * フォームバリデーションエラーのハンドリング
   * @param errors - フィールド名をキーとするバリデーションエラーのマップ
   * @description フォームのバリデーションエラーを統一的に処理し、ユーザーに通知します
   * @example
   * ```ts
   * errorHandler.handleValidationError({
   *   email: { message: 'メールアドレスの形式が正しくありません' },
   *   password: { message: 'パスワードは8文字以上必要です' }
   * });
   * ```
   */
  public handleValidationError(
    errors: Record<string, { message?: string } | string | unknown>
  ): void {
    const errorMessages = Object.values(errors)
      .filter(Boolean)
      .map((error: { message?: string } | string | unknown) => {
        if (typeof error === 'string') {
          return error;
        }
        if (error && typeof error === 'object' && 'message' in error) {
          return (error as { message?: string }).message || String(error);
        }
        return String(error);
      })
      .join(', ');

    const error = new AppError(
      errorMessages || '入力内容に誤りがあります。',
      ErrorType.VALIDATION,
      ErrorLevel.WARNING,
      'VALIDATION_ERROR',
      errors
    );

    this.handleError(error);
  }

  /**
   * エラーの正規化
   * @param error - 正規化対象のエラーオブジェクト
   * @returns 正規化されたAppErrorオブジェクト
   * @description 標準のErrorオブジェクトをAppErrorに変換し、エラータイプを判定します
   */
  private normalizeError(error: Error | AppError): AppError {
    if (error instanceof AppError) {
      return error;
    }

    // ネットワークエラーの判定
    if (error.message.includes('fetch') || error.message.includes('network')) {
      return new AppError(
        ERROR_MESSAGES[ErrorType.NETWORK],
        ErrorType.NETWORK,
        ErrorLevel.ERROR,
        'NETWORK_ERROR',
        error
      );
    }

    // その他のエラー
    return new AppError(
      error.message || ERROR_MESSAGES[ErrorType.UNKNOWN],
      ErrorType.UNKNOWN,
      ErrorLevel.ERROR,
      'GENERIC_ERROR',
      error
    );
  }

  /**
   * エラーログへの追加
   * @param error - ログに追加するAppErrorオブジェクト
   * @description エラーを内部ログに追加し、ログサイズの上限を管理します
   */
  private addToLog(error: AppError): void {
    this.errorLog.push(error);

    // ログサイズの制限
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog.shift();
    }
  }

  /**
   * トースト通知の表示
   * @param message - 表示するメッセージ
   * @param severity - トーストの重要度（info, warning, error）
   * @description Reduxストアを介してユーザーにトースト通知を表示します
   */
  private showToast(message: string, severity: ToastSeverity): void {
    store.dispatch(
      addToast({
        message,
        severity,
      })
    );
  }

  /**
   * サーバーへのエラーレポート
   * @param error - サーバーに報告するAppErrorオブジェクト
   * @description 本番環境でのみ、クリティカルなエラーをサーバーに送信します
   */
  private async reportToServer(error: AppError): Promise<void> {
    // 本番環境でのみレポート
    if (process.env.NODE_ENV !== 'production') {
      return;
    }

    try {
      // エラーレポートAPIへの送信
      const errorReport = {
        message: error.message,
        type: error.type,
        level: error.level,
        code: error.code,
        timestamp: error.timestamp,
        userAgent: navigator.userAgent,
        url: window.location.href,
        stack: error.stack,
        details: error.details,
      };

      // httpClientを動的にインポートして循環依存を回避
      const { httpClient } = await import('@src/api/core');
      const { API_ENDPOINTS } = await import('@src/constants/apiEndpoints');

      // エラーレポート送信（失敗しても問題ない）
      await httpClient.post(API_ENDPOINTS.ERROR_REPORT.SUBMIT, errorReport);
    } catch (reportError) {
      // エラーレポートの送信失敗は致命的ではないため、コンソールに記録するのみ
      console.error('Failed to report error:', reportError);
    }
  }

  /**
   * エラーログの取得
   * @returns 読み取り専用のエラーログ配列
   * @description 記録されているすべてのエラーログを取得します（イミュータブル）
   */
  public getErrorLog(): readonly AppError[] {
    return Object.freeze([...this.errorLog]);
  }

  /**
   * エラーログのクリア
   * @description 保存されているすべてのエラーログを削除します
   */
  public clearErrorLog(): void {
    this.errorLog = [];
  }

  /**
   * 特定のエラータイプのログを取得
   * @param type - フィルタリングするエラータイプ
   * @returns 指定されたタイプのエラーログ配列
   * @description 特定のエラータイプのみをフィルタリングして取得します
   */
  public getErrorsByType(type: ErrorType): AppError[] {
    return this.errorLog.filter((error) => error.type === type);
  }

  /**
   * 最新のエラーを取得
   * @returns 最新のAppErrorオブジェクト、またはログが空の場合はnull
   * @description 最後に記録されたエラーを取得します
   */
  public getLatestError(): AppError | null {
    return this.errorLog[this.errorLog.length - 1] || null;
  }
}

// デフォルトインスタンスのエクスポート
export const errorHandler = ErrorHandlerService.getInstance();

// 便利なヘルパー関数
const _handleError = (error: Error | AppError) => errorHandler.handleError(error);
const _handleHttpError = (response: Response) => errorHandler.handleHttpError(response);
const _handleValidationError = (errors: Record<string, any>) =>
  errorHandler.handleValidationError(errors);
